import { type KnownHighlightsMap, KnownHighlightStatus } from '../types/knownHighlights';
import Renderer from '../utils/Renderer';

const checkNodeIsOrContainsHighlightElement = (
  node: Node,
  highlightElementsSelector: string,
): boolean => {
  if (!(node instanceof HTMLElement)) {
    return false;
  }
  return node.matches(highlightElementsSelector) || node.querySelector(highlightElementsSelector) !== null;
};

/*
  Detects the DOM change that breaks highlights on sites which hydrate the whole document with React
  (hydrateRoot(document, …)): the framework reconciles <body> and removes our injected <rw-highlight>
  elements. They can be removed directly, or — when a whole content subtree is replaced — as
  descendants of a removed ancestor, so we check both the removed node and its subtree.
*/
export const checkMutationsRemoveHighlightElements = (
  mutations: MutationRecord[],
  highlightElementsSelector: string,
): boolean =>
  mutations.some((mutation) =>
    Array.from(mutation.removedNodes).some((node) =>
      checkNodeIsOrContainsHighlightElement(node, highlightElementsSelector)));

/*
  Returns ids of highlights the content frame believes are rendered but whose elements are no longer
  in the DOM. Rendered state is tracked in memory, so an external DOM wipe otherwise goes unnoticed and
  the highlights are never re-added.
*/
export const getRenderedHighlightIdsMissingFromDom = ({
  containerNode,
  knownHighlights,
}: {
  containerNode: HTMLElement;
  knownHighlights: KnownHighlightsMap;
}): string[] =>
  Object.values(knownHighlights)
    .filter(
      (knownHighlight) =>
        knownHighlight.status === KnownHighlightStatus.Rendered &&
        containerNode.querySelector(Renderer.getHighlightElementSelector(knownHighlight.id)) === null,
    )
    .map((knownHighlight) => knownHighlight.id);
